import { DatePicker, Row, Select } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
	getDefaultDateRanges,
	getTimeZoneOptions,
	myTimeZone,
} from '../../utils/DataFormatterUtils';
import { FILTER_CONFIG_NAMES } from '../../utils/DataConstants';
import 'moment-timezone'; // loads zone data for the .tz() calls below

const RANGE_PICKER_DATE_FORMAT = 'MM-DD-YYYY';
/** With `showTime`, the same date plus the hour and minute. */
const RANGE_PICKER_DATE_TIME_FORMAT = 'MM-DD-YYYY HH:mm';

require('./OWDateRangePicker.less');

const OWDateRangePicker: FC<any> = ({
	value,
	onChange,
	ranges = getDefaultDateRanges(),
	disabledDate = () => {},
	showTimeZone = false,
	/**
	 * Offer the time of day as well as the date.
	 *
	 * Off by default, because every caller before this one wants whole days and
	 * a picker that asks for minutes would make them say 00:00 to mean it. A
	 * feed of events is the case that needs it: narrowing to an afternoon is a
	 * different question from narrowing to a day.
	 */
	showTime = false,
	timeZones,
}): React.ReactElement => {
	const timeZoneOptions = useMemo(() => getTimeZoneOptions(timeZones), [timeZones]);

	const startDate = useMemo(() => value && value.length > 1 && value[0], [value]);
	const endDate = useMemo(() => value && value.length > 1 && value[1], [value]);
	const initialTimeZone = useMemo(
		() => (value && value.length > 2 && value[2]) || myTimeZone(),
		[value]
	);

	const [timeZone, setTimeZone] = useState(initialTimeZone);

	// `any`: the picker wants a two-element tuple and this is built from an
	// untyped `value`. Adding `showTime` changed which overload is chosen, which
	// is what surfaced it — the shape itself is unchanged.
	const vals: any = value && [startDate, endDate];
	/*
	  The remount key has to carry whatever the picker can show. Keyed to the day
	  alone, two different times on one day looked identical to React and the
	  second never reached the input.
	*/
	const keyFormat = showTime ? 'YYMMDDHHmm' : 'YYMMDD';

	const handleTimeZoneChange = (tz) => {
		setTimeZone(tz);
		const startDate = (vals && vals[0] && vals[0].tz(tz, true)) || undefined;
		const endDate = (vals && vals[1] && vals[1].tz(tz, true)) || undefined;
		(startDate || endDate) && onChange([startDate, endDate], tz);
	};

	return (
		<Row style={{ flex: 1 }}>
			<DatePicker.RangePicker
				key={
					startDate && endDate ? startDate.format(keyFormat) + endDate.format(keyFormat) : undefined
				}
				value={vals}
				ranges={ranges}
				format={showTime ? RANGE_PICKER_DATE_TIME_FORMAT : RANGE_PICKER_DATE_FORMAT}
				showTime={showTime ? { format: 'HH:mm' } : false}
				onChange={(v) => {
					onChange(v, timeZone);
				}}
				allowEmpty={[true, true]}
				disabledDate={disabledDate}
				style={showTimeZone ? { borderRadius: '2px 0px 0px 2px' } : {}}
			/>
			{showTimeZone ? (
				<Select
					className="tz-select"
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						whiteSpace: 'nowrap',
						width: 75,
						marginLeft: -1,
						borderRadius: '0px 2px 2px 0px',
					}}
					defaultValue={initialTimeZone}
					onSelect={handleTimeZoneChange}
				>
					{timeZoneOptions.map((option) => (
						<Select.Option key={option.value} value={option.value} label={option.label}>
							{option.label}
						</Select.Option>
					))}
				</Select>
			) : null}
		</Row>
	);
};

export default OWDateRangePicker;
